import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import i18next from "i18next";
import Avatar from "@mui/material/Avatar";
import {ButtonBase, CircularProgress, Typography} from "@mui/material";
import {OptionFormater, StringToColor} from "../../Helpers";
import {SharedInputControl} from "../DerivedControls";
import PopoverComponent from "../Popover/Popover.Component";
import {useTranslation} from "react-i18next";

const parentTranslationPath = "ApprovalsTrackingView";
const translationPath = "";
const MentionsComponent = ({
  inputText,
  setInputText,
  setMentionFilter,
  mentionList,
  mentionsUuids,
  setMentionsUuids,
  isUsersLoading,
}) => {
  const {t} = useTranslation(parentTranslationPath);
  const [popoverAttachedWith, setPopoverAttachedWith] = useState(null);
  const searchTimerRef = useRef(null);
  const popoverAttachedRef = useRef(null);
  const inputRef = useRef(null); // Reference to the input field
  const inputPointerLocationRef = useRef(0);
  const inputIdRef = useRef(null);
  /**
   * @param item
   * @author Aladdin Al-awadat (a.alawadat@elevatus.io)
   * @Description this method is to handle the toggle for the action's popover
   */
  const popoverToggleHandler = useCallback((event = null) => {
    setPopoverAttachedWith((prev) =>
      (!prev && event) || (prev && !event) ? (event && event) || null : prev
    );
    inputIdRef.current = inputRef.current.querySelector(
      "#SharedInputControlRef--0---0-0-inputText"
    );
    if (!inputIdRef.current) return;
    inputPointerLocationRef.current = inputIdRef.current.selectionStart;
  }, []);

  const findActiveMention = (text) => {
    if (!text) return null;

    // Check if text ends with whitespace .. if so, no active mention
    if (/\s$/.test(text)) return null;

    // Split by whitespace and get the last "word"
    // This ensures we treat the entire continuous string (including @ symbols) as one unit
    const parts = text.split(/\s/);
    const lastPart = parts[parts.length - 1];

    if (!lastPart || !lastPart.startsWith("@")) return null;

    const searchTerm = lastPart.slice(1);
    const startIndex = text.lastIndexOf(lastPart);

    return {searchTerm, startIndex};
  };

  const handleInputChange = ({value}) => {
    setInputText(value);

    const activeMention = findActiveMention(value);

    if (activeMention) {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

      popoverToggleHandler(popoverAttachedRef.current);

      // Update the mention filter for searching
      setMentionFilter((prev) => ({
        ...prev,
        search: activeMention.searchTerm,
        page: 1,
      }));
    } else {
      // Close the popover if no active mention is found
      popoverToggleHandler(null);
    }

    if (mentionsUuids.length > 0) {
      let updatedText = value || "";

      // Filter the mentionsUuids array to remove objects whose display names are removed from the text
      const updatedMentionsUuids = mentionsUuids.filter((mention) => {
        // Extract first name and last name from the mention's display
        const [firstName, lastName] = mention.display.split(" ");

        // Build a regex to check if the mention still exists in the updated text
        const fullMentionRegex = new RegExp(`@${firstName}\\s${lastName}`, "g");
        const fullMentionExists = updatedText?.match(fullMentionRegex);

        if (!fullMentionExists) {
          // If the full mention doesn't exist, remove it from the text
          const partialMentionRegex = new RegExp(
            `@${firstName}\\s?${lastName}?`,
            "g"
          );
          updatedText = updatedText.replace(partialMentionRegex, "").trim();

          return false;
        }

        return true;
      });

      if (updatedText !== value) {
        setInputText(updatedText);
      }

      if (
        JSON.stringify(updatedMentionsUuids) !== JSON.stringify(mentionsUuids)
      ) {
        setMentionsUuids(updatedMentionsUuids);
      }
    }
  };

  useEffect(() => {
    if (popoverAttachedWith) {
      setTimeout(() => {
        inputIdRef.current.focus();
        inputIdRef.current.setSelectionRange(
          inputPointerLocationRef.current,
          inputPointerLocationRef.current
        );
      });
    }
  }, [popoverAttachedWith]);

  return (
    <>
      <div ref={popoverAttachedRef}></div>
      <SharedInputControl
        inputRef={inputRef}
        rows={3}
        multiline
        isFullWidth
        editValue={inputText}
        onValueChanged={handleInputChange}
        placeholder="Type @ to mention users"
        parentTranslationPath={""}
        stateKey={"inputText"}
      />
      <PopoverComponent
        disableEnforceFocus
        anchorOrigin={{
          vertical: "left",
          horizontal: "left",
        }}
        transformOrigin={{vertical: "left", horizontal: "bottom"}}
        idRef="blocksActionsPopoverRef"
        attachedWith={popoverAttachedWith}
        handleClose={() => popoverToggleHandler(null)}
        popoverClasses="blocks-actions-popover-wrapper"
        component={
          <div
            style={{
              maxHeight: "175px",
              overflowY: "auto",
              maxWidth: "18vw",
              minWidth: "18vh",
            }}
          >
            <>
              {mentionList.results.length === 0 ? (
                <Typography className="px-3 py-2">
                  {t(`${translationPath}no-options`)}
                </Typography>
              ) : (
                mentionList.results.map((item) => (
                  <div
                    key={`${item.uuid}-${inputText}`}
                    className={"d-inline-flex w-100 py-1"}
                  >
                    <ButtonBase
                      className="btns theme-transparent w-100"
                      onClick={() => {
                        setInputText((prevState) => {
                          const activeMention = findActiveMention(prevState);
                          let baseText = prevState || "";

                          if (activeMention) {
                            baseText = prevState
                              .substring(0, activeMention.startIndex)
                              .trim();
                          }

                          const fullName = [
                            item.first_name[i18next.language],
                            item.last_name[i18next.language],
                          ].join(" ");

                          return `${baseText ? baseText + " " : ""}@${fullName} `;
                        });

                        setMentionsUuids((prevMentions) => {
                          const existingMentions = prevMentions || [];

                          // Avoid duplicate UUIDs
                          if (
                            !existingMentions.some(
                              (mention) => mention.uuid === item.uuid
                            )
                          ) {
                            const fullName = [
                              item.first_name[i18next.language],
                              item.last_name[i18next.language],
                            ].join(" ");

                            return [
                              ...existingMentions,
                              {
                                uuid: item.uuid,
                                display: fullName,
                              },
                            ];
                          }

                          return existingMentions;
                        });

                        setMentionFilter({search: "", page: 1});
                        popoverToggleHandler(null);
                      }}
                    >
                      <div className={"d-flex justify-content-start w-100"}>
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            fontSize: 14,
                            backgroundColor: StringToColor(
                              `${item.first_name[i18next.language] || ""}`
                            ),
                          }}
                        >
                          {`${item.first_name[i18next.language]} ${
                            item.last_name[i18next.language]
                          }`
                            .split(" ")
                            .map((word) => word[0])}
                        </Avatar>
                        <Typography className={"px-1"}>
                          {OptionFormater(item, i18next.language)}
                        </Typography>
                      </div>
                    </ButtonBase>
                  </div>
                ))
              )}
              {mentionList?.paginate?.page <
                mentionList?.paginate?.lastPage && (
                <ButtonBase
                  className="btns theme-transparent"
                  onClick={() => {
                    setMentionFilter((items) => ({
                      ...items,
                      page: items.page + 1,
                    }));
                  }}
                >
                  {isUsersLoading && (
                    <div className="mx-2 c-gray">
                      <CircularProgress size={20} color="inherit" />
                    </div>
                  )}
                  <span>{t(`${translationPath}load-more`)}</span>
                </ButtonBase>
              )}
            </>
          </div>
        }
      />
    </>
  );
};

MentionsComponent.propTypes = {
  inputText: PropTypes.string,
  setInputText: PropTypes.func.isRequired,
  setMentionFilter: PropTypes.func.isRequired,
  mentionList: PropTypes.shape({
    results: PropTypes.arrayOf(
      PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        first_name: PropTypes.object,
        last_name: PropTypes.object,
      })
    ),
    paginate: PropTypes.shape({
      page: PropTypes.number,
      total: PropTypes.number,
      lastPage: PropTypes.number,
    }),
  }).isRequired,
  setMentionsUuids: PropTypes.func.isRequired,
  mentionsUuids: PropTypes.array.isRequired,
  isUsersLoading: PropTypes.bool,
};

MentionsComponent.defaultProps = {
  inputText: "",
  isLoading: false,
  mentionsUuids: [],
};

export default MentionsComponent;
